import * as React from 'react'

interface SubNavigationBarProps {
  children?: React.ReactNode
}

export const SubNavigationBar = ({ children }: SubNavigationBarProps) => {
  const navRef = React.useRef<HTMLElement | null>(null)

  const isDown = React.useRef(false)
  const didDrag = React.useRef(false)

  const startX = React.useRef(0)
  const startScrollLeft = React.useRef(0)

  const lastX = React.useRef(0)
  const lastT = React.useRef(0)
  const velocity = React.useRef(0)
  const raf = React.useRef<number | null>(null)

  const [grabbing, setGrabbing] = React.useState(false)

  const stopRaf = () => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (!navRef.current) return
    if (e.button !== 0) return

    isDown.current = true
    didDrag.current = false
    setGrabbing(true)

    const rect = navRef.current.getBoundingClientRect()
    startX.current = e.clientX - rect.left
    startScrollLeft.current = navRef.current.scrollLeft

    lastX.current = e.clientX
    lastT.current = performance.now()
    velocity.current = 0

    // Prevent text selection / native drag image
    e.preventDefault()
  }

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDown.current) return
      if (!navRef.current) return

      const rect = navRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const walk = x - startX.current

      // Start "dragging" after a tiny threshold
      if (!didDrag.current && Math.abs(walk) > 2) didDrag.current = true

      // Velocity (optional, useful if you later add inertia)
      const now = performance.now()
      const dt = Math.max(1, now - lastT.current)
      const dx = e.clientX - lastX.current
      velocity.current = dx / dt
      lastX.current = e.clientX
      lastT.current = now

      stopRaf()
      raf.current = requestAnimationFrame(() => {
        if (!navRef.current) return
        navRef.current.scrollLeft = startScrollLeft.current - walk
      })

      e.preventDefault()
    }

    const onUp = () => {
      if (!isDown.current) return
      isDown.current = false
      setGrabbing(false)
      stopRaf()

      // Cancel accidental click on links if there was a drag
      if (didDrag.current) {
        const killClick = (evt: MouseEvent) => {
          evt.preventDefault()
          evt.stopPropagation()
        }
        window.addEventListener('click', killClick, { capture: true, once: true })
      }
    }

    window.addEventListener('mousemove', onMove, { passive: false })
    window.addEventListener('mouseup', onUp)

    return () => {
      window.removeEventListener('mousemove', onMove as any)
      window.removeEventListener('mouseup', onUp)
      stopRaf()
    }
  }, [])

  return (
    <div className="tw-sticky tw-top-0 tw-z-5 tw-max-h-[54px] tw-border-b tw-border-base-300 tw-bg-content-primary tw-px-1">
      <nav
        ref={navRef}
        className={[
          'tw-scrollbar-hidden tw-flex tw-w-full tw-select-none tw-items-end tw-overflow-x-auto',
          grabbing ? 'tw-cursor-grabbing' : 'tw-cursor-grab',
        ].join(' ')}
        onMouseDown={onMouseDown}
        // Helpful for touchpads / mobile, doesn't break mouse:
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </nav>
    </div>
  )
}
